import styled from "styled-components";
import InputWithLabel from "shared/components/InputWithLabel";
import RegisterFooter from "shared/components/RegisterFooter";
import useCounselRegister from "features/teacher/hooks/counsel/useCounselRegister";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "shared/components/Modal";
import { hw, scrollbar } from "features/homework/homeworkUi";

const CounselRegister = () => {
    const navigate = useNavigate();
    const studentUid = useLocation().state;
    const {
        student, onChange, inputs, onSaveClick,
        isSucceed, isError, isOpenNullModal,
        setIsError, setIsOpenNullModal,
    } = useCounselRegister(studentUid);

    return (
        <Container>
            <Wrapper>
                <Header>
                    <div>
                        <Title>상담 등록</Title>
                        <Desc>상담일과 내용을 입력한 뒤 저장하세요.</Desc>
                    </div>
                    <Close type="button" onClick={() => navigate("..")}>닫기</Close>
                </Header>
                <Content>
                    <Modal isOpen={isOpenNullModal} title="ERROR" content="필수 입력란을 모두 입력해 주세요" onClose={() => setIsOpenNullModal(false)} />
                    <Modal isOpen={isError} title="ERROR" content="상담 저장중 오류가 발생하였습니다" onClose={() => setIsError(false)} />
                    <Modal isOpen={isSucceed} title="SUCCESS" content="상담이 저장되었습니다" onClose={() => navigate("..")} />
                    <MetaCard>
                        <InputWithLabel disabled={true} value={student} label="이름" />
                        <Field>
                            <Label htmlFor="counsel-date">상담일</Label>
                            <DateInput
                                id="counsel-date"
                                type="date"
                                name="counsel_date"
                                value={inputs.counsel_date}
                                onChange={onChange}
                            />
                        </Field>
                    </MetaCard>
                    <NoteCard>
                        <NoteHead $accent>상담내용</NoteHead>
                        <TextArea
                            name="counsel_detail"
                            onChange={onChange}
                            value={inputs.counsel_detail}
                            placeholder="상담 내용을 입력해주세요"
                        />
                    </NoteCard>
                    <NoteCard>
                        <NoteHead>조치내용</NoteHead>
                        <TextArea
                            name="action_detail"
                            onChange={onChange}
                            value={inputs.action_detail}
                            placeholder="조치 내용을 입력해주세요"
                        />
                    </NoteCard>
                </Content>
                <RegisterFooter onSaveButtonClick={onSaveClick} />
            </Wrapper>
        </Container>
    );
};

const Container = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    padding: 20px 24px;
    z-index: 10;
    background: ${hw.bg};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 22px 24px 16px;
    border-radius: 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    box-shadow: ${hw.shadow};
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    color: ${hw.text};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
`;

const Desc = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Close = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
        background: ${hw.bg};
        color: ${hw.text};
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    ${scrollbar}
`;

const MetaCard = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 20px;
    background: ${hw.bg};
    padding: 20px 22px;
    border-radius: 14px;
    border: 1px solid ${hw.border};
    flex-shrink: 0;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.label`
    color: ${hw.text};
    font-size: 14px;
    font-weight: 500;
`;

const DateInput = styled.input`
    height: 44px;
    padding: 0 12px;
    border: 1px solid ${hw.border};
    border-radius: 8px;
    font-size: 14px;
    color: ${hw.text};
    background: ${hw.surface};
    width: 100%;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

const NoteCard = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 160px;
    border: 1px solid ${hw.border};
    border-radius: 14px;
    overflow: hidden;
    background: ${hw.surface};
`;

const NoteHead = styled.div<{ $accent?: boolean }>`
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.$accent ? hw.brand : hw.text};
    background: ${props => props.$accent ? hw.brandSoft : "#FAFBFC"};
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const TextArea = styled.textarea`
    width: 100%;
    flex: 1;
    min-height: 120px;
    resize: none;
    padding: 14px 16px;
    font-size: 14px;
    line-height: 1.6;
    border: none;
    background: transparent;
    color: ${hw.text};
    box-sizing: border-box;
    ${scrollbar}

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: ${hw.textSubtle};
    }
`;

export default CounselRegister;
